import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list, type Client, type Engagement } from '../lib/api'
import { Empty, PageHead, Skel } from '../components/ui'
import { thaiDateTime } from '../lib/format'

type ERPSource = 'sap' | 'oracle' | 'dynamics' | 'csv' | 'generic'

interface ERPMapping {
  occurred_at: string
  amount: string
  currency: string
  direction: string
  counterparty_id: string
  counterparty_name: string
  description: string
  reference: string
  channel: string
}

interface ERPConnector {
  id: string
  name: string
  source: ERPSource
  mapping: ERPMapping
  client_id?: string
  engagement_id?: string
  enabled: boolean
  last_run_at?: string
  created_at: string
}

interface ERPRowError {
  row: number
  column?: string
  message: string
}

interface ERPIngestResult {
  run_id: string
  connector_id: string
  received: number
  inserted: number
  skipped: number
  error_count: number
  errors: ERPRowError[]
}

interface FormState {
  id: string
  name: string
  source: ERPSource
  client_id: string
  engagement_id: string
  enabled: boolean
  mapping: ERPMapping
}

const QUERY_KEY = ['erp-connectors']

const emptyMapping: ERPMapping = {
  occurred_at: '', amount: '', currency: '', direction: '',
  counterparty_id: '', counterparty_name: '', description: '', reference: '', channel: '',
}

const emptyForm: FormState = {
  id: '', name: '', source: 'csv', client_id: '', engagement_id: '', enabled: true,
  mapping: { ...emptyMapping },
}

const sourceLabel: Record<ERPSource, string> = {
  sap: 'SAP', oracle: 'Oracle', dynamics: 'Microsoft Dynamics', csv: 'ไฟล์ CSV', generic: 'ระบบอื่น ๆ',
}

const mappingFields: { key: keyof ERPMapping; label: string; hint: string; required?: boolean }[] = [
  { key: 'occurred_at', label: 'คอลัมน์วันที่', hint: 'วันที่ / date', required: true },
  { key: 'amount', label: 'คอลัมน์จำนวนเงิน', hint: 'จำนวนเงิน / amount', required: true },
  { key: 'direction', label: 'คอลัมน์ทิศทาง', hint: 'ทิศทาง / DR-CR' },
  { key: 'currency', label: 'คอลัมน์สกุลเงิน', hint: 'สกุลเงิน / currency' },
  { key: 'counterparty_id', label: 'รหัสคู่ค้า', hint: 'รหัสคู่ค้า / vendor id' },
  { key: 'counterparty_name', label: 'ชื่อคู่ค้า', hint: 'ชื่อคู่ค้า / counterparty' },
  { key: 'description', label: 'รายละเอียด', hint: 'รายละเอียด / description' },
  { key: 'reference', label: 'เลขที่อ้างอิง', hint: 'อ้างอิง / reference' },
  { key: 'channel', label: 'ช่องทาง', hint: 'ช่องทาง / channel' },
]

const sampleCSV = `วันที่,จำนวนเงิน,ทิศทาง,ชื่อคู่ค้า,รายละเอียด,อ้างอิง
31/12/2567,"1,234.50",ถอน,บริษัท ตัวอย่าง จำกัด,ค่าบริการ,INV-001
31/12/2567,"2,500.00",ฝาก,บริษัท ตัวอย่าง จำกัด,รับชำระหนี้,INV-002`

export function ERP() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: QUERY_KEY, queryFn: () => api<ERPConnector[]>('/v1/erp/connectors'), staleTime: 20_000 })
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => list<Client>('/v1/clients'), staleTime: 60_000 })
  const engagements = useQuery({ queryKey: ['engagements'], queryFn: () => list<Engagement>('/v1/engagements'), staleTime: 60_000 })

  const [form, setForm] = useState<FormState>(emptyForm)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [csv, setCsv] = useState('')
  const [result, setResult] = useState<ERPIngestResult | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const rows = q.data ?? []
  const active = rows.find((c) => c.id === activeId) ?? null
  const formEngagements = useMemo(
    () => (engagements.data ?? []).filter((e) => !form.client_id || e.client_id === form.client_id),
    [engagements.data, form.client_id],
  )

  function resetForm() {
    setForm({ ...emptyForm, mapping: { ...emptyMapping } })
  }

  function edit(c: ERPConnector) {
    setForm({
      id: c.id, name: c.name, source: c.source,
      client_id: c.client_id ?? '', engagement_id: c.engagement_id ?? '',
      enabled: c.enabled, mapping: { ...emptyMapping, ...c.mapping },
    })
    setError(null)
    setNotice(null)
  }

  const save = useMutation({
    mutationFn: (f: FormState) => apiWrite<ERPConnector>('/v1/erp/connectors', 'POST', {
      id: f.id || undefined,
      name: f.name.trim(),
      source: f.source,
      client_id: f.client_id,
      engagement_id: f.engagement_id,
      enabled: f.enabled,
      mapping: f.mapping,
    }),
    onSuccess: (saved) => {
      setNotice(`บันทึกโปรไฟล์ “${saved.name}” แล้ว`)
      setError(null)
      resetForm()
      void qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'บันทึกโปรไฟล์ไม่สำเร็จ'),
  })

  const remove = useMutation({
    mutationFn: (id: string) => apiWrite(`/v1/erp/connectors/${id}`, 'DELETE'),
    onSuccess: (_d, id) => {
      if (activeId === id) { setActiveId(null); setResult(null) }
      if (form.id === id) resetForm()
      setNotice('ลบโปรไฟล์แล้ว — รายการที่นำเข้าไปแล้วยังอยู่ในบัญชีแยกประเภท')
      setError(null)
      void qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'ลบโปรไฟล์ไม่สำเร็จ'),
  })

  const ingest = useMutation({
    mutationFn: (v: { id: string; body: string }) => api<ERPIngestResult>(`/v1/erp/connectors/${v.id}/ingest`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/csv; charset=utf-8' },
      body: v.body,
    }),
    onSuccess: (data) => {
      setResult(data)
      setError(null)
      setNotice(null)
      void qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: (e) => {
      setResult(null)
      setError(e instanceof Error ? e.message : 'นำเข้าข้อมูลไม่สำเร็จ')
    },
  })

  function submitForm() {
    if (!form.name.trim()) { setError('ต้องระบุชื่อโปรไฟล์'); return }
    if (!form.mapping.occurred_at.trim() || !form.mapping.amount.trim()) {
      setError('ต้องระบุคอลัมน์วันที่และคอลัมน์จำนวนเงิน')
      return
    }
    save.mutate(form)
  }

  async function pickFile(file: File | undefined) {
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { setError('ไฟล์ใหญ่เกิน 5 MB'); return }
    setCsv(await file.text())
    setError(null)
    setResult(null)
  }

  const selectedClient = clients.data?.find((c) => c.id === form.client_id)
  const canIngest = Boolean(active && csv.trim()) && !ingest.isPending

  return (
    <>
      <PageHead
        title="เชื่อมต่อ ERP"
        sub="นำเข้ารายการเดินบัญชีด้วยโปรไฟล์การแมปคอลัมน์ รองรับ SAP, Oracle, Dynamics และไฟล์ CSV ทั่วไป"
        actions={<span className="chip">{rows.length} โปรไฟล์</span>}
      />

      {error ? <div className="form-error" style={{ marginBottom: 14 }}>{error}</div> : null}
      {notice ? <div className="dim" style={{ marginBottom: 14, fontSize: 12 }}>{notice}</div> : null}

      <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 560px', minWidth: 320 }}>
          {q.isLoading ? (
            <div className="stack"><Skel h={54} /><Skel h={54} /><Skel h={54} /></div>
          ) : rows.length === 0 ? (
            <div className="card">
              <Empty title="ยังไม่มีโปรไฟล์เชื่อมต่อ" body="สร้างโปรไฟล์แรกทางด้านขวา แล้ววางหรืออัปโหลดไฟล์ CSV เพื่อนำเข้า" />
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>โปรไฟล์</th>
                    <th>แหล่งข้อมูล</th>
                    <th>ผูกกับ</th>
                    <th>สถานะ</th>
                    <th>ใช้งานล่าสุด</th>
                    <th style={{ width: 170 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => {
                    const client = clients.data?.find((x) => x.id === c.client_id)
                    const engagement = engagements.data?.find((x) => x.id === c.engagement_id)
                    return (
                      <tr key={c.id} style={c.id === activeId ? { background: 'rgba(200,169,106,0.05)' } : undefined}>
                        <td>
                          <div>{c.name}</div>
                          <div className="dim mono" style={{ fontSize: 10.5 }}>
                            {c.mapping.occurred_at} · {c.mapping.amount}
                          </div>
                        </td>
                        <td><span className="chip">{sourceLabel[c.source] ?? c.source}</span></td>
                        <td className="dim" style={{ fontSize: 12 }}>
                          {engagement?.title ?? client?.name ?? 'ยังไม่ผูก'}
                        </td>
                        <td><span className={`chip${c.enabled ? ' gold' : ''}`}>{c.enabled ? 'เปิดใช้' : 'ปิดใช้'}</span></td>
                        <td className="dim" style={{ fontSize: 12 }}>
                          {c.last_run_at ? thaiDateTime(c.last_run_at) : 'ยังไม่เคยนำเข้า'}
                        </td>
                        <td>
                          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                            <button className="btn btn-ghost btn-sm"
                              onClick={() => { setActiveId(c.id); setResult(null); setError(null) }}>นำเข้า</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => edit(c)}>แก้ไข</button>
                            <button className="btn btn-ghost btn-sm"
                              onClick={() => { if (window.confirm(`ลบโปรไฟล์ “${c.name}” ?`)) remove.mutate(c.id) }}>ลบ</button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="card" style={{ marginTop: 18, padding: 18 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <div>
                <div className="eyebrow">นำเข้าข้อมูล</div>
                <div className="heading-m" style={{ marginTop: 6 }}>
                  {active ? active.name : 'เลือกโปรไฟล์จากตาราง'}
                </div>
              </div>
              {active ? <span className="chip" style={{ marginLeft: 'auto' }}>{sourceLabel[active.source]}</span> : null}
            </div>

            <p className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
              วางข้อความ CSV หรือเลือกไฟล์ ไม่เกิน 5 MB · แถวแรกเป็นชื่อคอลัมน์ · ระบบจะข้ามแถวที่ผิดและรายงานกลับ
            </p>

            <textarea
              className="input"
              style={{ width: '100%', minHeight: 150, marginTop: 12, fontFamily: 'var(--font-mono, monospace)', fontSize: 11.5 }}
              placeholder={sampleCSV}
              value={csv}
              onChange={(e) => { setCsv(e.target.value); setResult(null) }}
              disabled={!active}
              aria-label="ข้อมูล CSV"
            />

            <div className="row" style={{ marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
              <input ref={fileRef} type="file" accept=".csv,text/csv" hidden
                onChange={(e) => { void pickFile(e.target.files?.[0]); e.target.value = '' }} />
              <button className="btn btn-ghost btn-sm" disabled={!active} onClick={() => fileRef.current?.click()}>
                เลือกไฟล์ CSV
              </button>
              <button className="btn btn-ghost btn-sm" disabled={!active} onClick={() => setCsv(sampleCSV)}>
                ใส่ตัวอย่าง
              </button>
              <button
                className="btn btn-primary btn-sm"
                style={{ marginLeft: 'auto' }}
                disabled={!canIngest}
                onClick={() => { if (active) ingest.mutate({ id: active.id, body: csv }) }}
              >
                {ingest.isPending ? 'กำลังนำเข้า…' : 'ตรวจสอบและนำเข้า'}
              </button>
            </div>

            {result ? (
              <div style={{ marginTop: 18 }}>
                <div className="rule" style={{ marginBottom: 14 }} />
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <span className="chip">รับเข้า {result.received} แถว</span>
                  <span className="chip gold">บันทึก {result.inserted} รายการ</span>
                  <span className="chip">ข้าม {result.skipped} รายการซ้ำ</span>
                  <span className={`chip${result.error_count > 0 ? ' sev-high' : ''}`}>ผิดพลาด {result.error_count} แถว</span>
                </div>
                {result.errors.length > 0 ? (
                  <div className="table-wrap" style={{ marginTop: 12 }}>
                    <table className="data">
                      <thead><tr><th style={{ width: 70 }}>แถว</th><th style={{ width: 110 }}>คอลัมน์</th><th>สาเหตุ</th></tr></thead>
                      <tbody>
                        {result.errors.map((e, i) => (
                          <tr key={`${e.row}-${e.column ?? ''}-${i}`}>
                            <td className="tnum">{e.row}</td>
                            <td className="mono dim" style={{ fontSize: 11 }}>{e.column ?? '—'}</td>
                            <td style={{ fontSize: 12 }}>{e.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {result.error_count > result.errors.length ? (
                  <p className="dim" style={{ fontSize: 11, marginTop: 8 }}>
                    แสดง {result.errors.length} จาก {result.error_count} แถวที่ผิดพลาด — รายละเอียดทั้งหมดถูกเก็บในรอบนำเข้า
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="card" style={{ flex: '0 1 380px', minWidth: 300, padding: 20 }}>
          <div className="eyebrow">{form.id ? 'แก้ไขโปรไฟล์' : 'สร้างโปรไฟล์ใหม่'}</div>
          <div className="heading-m" style={{ marginTop: 6 }}>โปรไฟล์การแมปคอลัมน์</div>
          <p className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
            ระบุชื่อคอลัมน์ให้ตรงกับไฟล์ต้นทาง รองรับหัวตารางภาษาไทยและอังกฤษ
          </p>

          <div style={{ marginTop: 16 }}>
            <label className="dim" style={{ fontSize: 11 }} htmlFor="erp-name">ชื่อโปรไฟล์</label>
            <input id="erp-name" className="input" style={{ width: '100%', marginTop: 5 }}
              value={form.name} maxLength={120}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="เช่น SAP ประจำปี 2567" />
          </div>

          <div style={{ marginTop: 12 }}>
            <label className="dim" style={{ fontSize: 11 }} htmlFor="erp-source">แหล่งข้อมูล</label>
            <select id="erp-source" className="select" style={{ width: '100%', marginTop: 5 }}
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value as ERPSource })}>
              {(Object.keys(sourceLabel) as ERPSource[]).map((s) => <option key={s} value={s}>{sourceLabel[s]}</option>)}
            </select>
          </div>

          <div style={{ marginTop: 12 }}>
            <label className="dim" style={{ fontSize: 11 }} htmlFor="erp-client">นิติบุคคล</label>
            <select id="erp-client" className="select" style={{ width: '100%', marginTop: 5 }}
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value, engagement_id: '' })}>
              <option value="">— ไม่ระบุ —</option>
              {(clients.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div style={{ marginTop: 12 }}>
            <label className="dim" style={{ fontSize: 11 }} htmlFor="erp-engagement">โครงการตรวจสอบ (ไม่บังคับ)</label>
            <select id="erp-engagement" className="select" style={{ width: '100%', marginTop: 5 }}
              value={form.engagement_id}
              onChange={(e) => setForm({ ...form, engagement_id: e.target.value })}>
              <option value="">— ไม่ระบุ —</option>
              {formEngagements.map((e) => <option key={e.id} value={e.id}>{e.code} · {e.title}</option>)}
            </select>
            {!form.client_id && form.engagement_id ? (
              <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>
                ระบบจะใช้นิติบุคคลของโครงการนี้โดยอัตโนมัติ
              </div>
            ) : null}
          </div>

          <div className="rule" style={{ margin: '16px 0' }} />

          {mappingFields.map((f) => (
            <div key={f.key} style={{ marginTop: 10 }}>
              <label className="dim" style={{ fontSize: 11 }} htmlFor={`erp-map-${f.key}`}>
                {f.label}{f.required ? <span style={{ color: 'var(--color-gold)' }}> *</span> : null}
              </label>
              <input id={`erp-map-${f.key}`} className="input" style={{ width: '100%', marginTop: 4 }}
                value={form.mapping[f.key]}
                placeholder={f.hint}
                onChange={(e) => setForm({ ...form, mapping: { ...form.mapping, [f.key]: e.target.value } })} />
            </div>
          ))}

          <label className="row" style={{ marginTop: 16, gap: 8, fontSize: 12 }}>
            <input type="checkbox" checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            เปิดใช้งานโปรไฟล์นี้
          </label>

          <div className="row" style={{ marginTop: 18, gap: 8 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} disabled={save.isPending} onClick={submitForm}>
              {save.isPending ? 'กำลังบันทึก…' : form.id ? 'บันทึกการแก้ไข' : 'สร้างโปรไฟล์'}
            </button>
            {form.id ? <button className="btn btn-ghost" onClick={resetForm}>ยกเลิก</button> : null}
          </div>
          {selectedClient ? (
            <p className="dim" style={{ fontSize: 10.5, marginTop: 8 }}>ผูกกับ {selectedClient.name}</p>
          ) : null}
        </div>
      </div>
    </>
  )
}
